<template>
  <article class="event-users">
    <h3 class="event-users__header">
      {{ $t("event.users.title") }}

      <icon name="event-users-header" class="event-users__header-icon" />
    </h3>

    <div class="event-users__count">
      {{ $t("event.users.total") }}

      {{ countAllUsers }}
      {{ countAllUsers | pluralize($t("event.users.pluralize")) }}
    </div>
    <Notification
      v-if="hasUserAdminRights"
      modify="_flex"
      icon="warning-border--no-color"
      >{{ $t("event.users.notification") }}</Notification
    >
    <div class="event-users__content">
      <div class="event-users__content-section">
        <div class="event-users__user" v-if="countAdminAccessUsers <= 1">
          <h4 class="event-users__user-title">{{ $t("event.users.admin") }}</h4>

          <p class="event-users__user-text">{{ adminUser }}</p>
        </div>

        <div class="event-users__user" v-else>
          <h4 class="event-users__user-title">
            {{ $t("event.users.controlAccess") }}
          </h4>

          <p class="event-users__user-text">
            {{ countAdminAccessUsers }}

            {{ countAdminAccessUsers | pluralize($t("event.users.pluralize")) }}
          </p>
        </div>
      </div>

      <div class="event-users__content-section">
        <div class="event-users__user event-users__user_read">
          <h4 class="event-users__user-title">
            {{ $t("event.users.writeAccess") }}
          </h4>

          <p class="event-users__user-text">
            {{ countWriteAccessUsers }}
            {{ countWriteAccessUsers | pluralize($t("event.users.pluralize")) }}
          </p>
        </div>

        <div class="event-users__user event-users__user_write">
          <h4 class="event-users__user-title">
            {{ $t("event.users.readAccess") }}
          </h4>

          <p class="event-users__user-text">
            {{ countReadAccessUsers }}
            {{ countReadAccessUsers | pluralize($t("event.users.pluralize")) }}
          </p>
        </div>

        <div class="event-users__action" style="margin-top: 38px;">
          <template v-if="hasUserAdminRights">
            <PrimaryButton
              @click="$modal.show('event-modal-users')"
              v-if="!countWriteAccessUsers && !countReadAccessUsers"
            >
              {{ $t("event.users.addUserBtn") }}
            </PrimaryButton>

            <LinkIcon
              icon="link-icon-edit"
              @click="$modal.show('event-modal-users')"
              v-if="countWriteAccessUsers || countReadAccessUsers"
              :bold="true"
            >
              {{ $t("event.users.manageUsersBtn") }}
            </LinkIcon>
          </template>

          <template v-else>
            <LinkIcon
              icon="look"
              @click="$modal.show('event-modal-users')"
              :bold="true"
            >
              {{ $t("event.users.lookUsersBtn") }}
            </LinkIcon>
          </template>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { i18n } from "@v2/shared/config";
import {
  PrimaryButton,
  LinkIcon,
  Notification
} from "@v2/shared/ui/components";

export default {
  name: "EventUsers",

  components: {
    Notification,
    PrimaryButton,
    LinkIcon
  },

  computed: {
    ...mapState("event", ["event"]),
    ...mapGetters("event", ["hasUserAdminRights"]),

    locale() {
      return i18n.locale;
    },

    adminUser() {
      if (!this.event.users || !this.event.users.length) {
        return "";
      }

      const user = this.event.users.find(x => x.role === "supervisor");

      if (!user) {
        return "";
      }

      return `${user.lastname || ""} ${user.name}`;
    },

    countAdminAccessUsers() {
      if (!this.event.users || !this.event.users.length) {
        return 0;
      }

      return this.event.users.filter(x =>
        ["supervisor", "admin"].includes(x.role)
      ).length;
    },

    countWriteAccessUsers() {
      if (!this.event.users || !this.event.users.length) {
        return 0;
      }

      return this.event.users.filter(x => x.role === "edit").length;
    },

    countReadAccessUsers() {
      if (!this.event.users || !this.event.users.length) {
        return 0;
      }

      return this.event.users.filter(x => x.role === "read").length;
    },

    countAllUsers() {
      if (!this.event.users) {
        return 0;
      }

      return this.event.users.length;
    }
  }
};
</script>

<style lang="scss">
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.event-users {
  width: 620px;

  @include respond-down(lg) {
    width: 442px;
  }

  margin-top: 24px;

  box-sizing: border-box;

  position: relative;

  display: flex;
  flex-direction: column;

  @include respond-down(md) {
    margin-top: 0;
  }

  &__header {
    @include h2();

    padding: 20px 30px;

    @include respond-down(md) {
      padding: 15px 15px 34px 15px;
      font-size: 1rem;
    }

    border-bottom: 1px solid var(--gray-darker);

    position: relative;

    &-icon {
      position: absolute;
      right: 15px;
      top: 50%;

      transform: translateY(-50%);

      width: 80px;
      height: 80px;

      @include respond-down(md) {
        width: 56px;
        height: 56px;
      }
    }
  }

  &__count {
    position: absolute;
    top: 26px;
    left: 210px;

    @include respond-down(lg) {
      top: 30px;
      left: 190px;
    }

    @include respond-down(md) {
      top: 45px;
      left: 15px;
    }

    font-size: 12px;
    font-weight: normal;
    font-style: normal;
    line-height: 18px;
    color: #a3a8b3;
  }

  &__content {
    height: 100%;

    display: flex;
    align-items: stretch;

    @include respond-down(md) {
      flex-direction: column;
      height: auto;
    }

    &-section {
      &:first-child {
        padding: 30px;
        border-right: 1px solid var(--gray-darker);
        width: 182px;
        box-sizing: border-box;

        flex-shrink: 0;

        @include respond-down(lg) {
          width: 154px;
        }

        @include respond-down(md) {
          width: 100%;
          border-right: none;
          border-bottom: 1px solid #f1f1f1;
          padding: 15px;
        }
      }

      &:last-child {
        padding: 30px;

        @include respond-down(md) {
          padding: 15px;
        }

        display: flex;
        justify-content: space-between;

        flex-grow: 1;
        flex-wrap: wrap;

        @include respond-down(md) {
          flex-direction: column;
        }
      }
    }
  }

  &__user {
    &_write {
      width: 157px;
    }

    &_read {
      width: 167px;
    }

    &_read,
    &_write {
      @include respond-down(lg) {
        width: 107px;
      }

      @include respond-down(md) {
        width: 100%;
        margin-bottom: 20px;

        br {
          display: none;
        }
      }
    }

    &-title {
      font-size: 14px;
      font-weight: 500;
      font-style: normal;
      font-stretch: normal;
      line-height: 1.43;
      letter-spacing: normal;
      color: #000000;
      margin: 0;

      @include respond-down(md) {
        font-size: 12px;
      }
    }

    &-text {
      font-size: 13px;
      font-weight: normal;
      font-style: normal;
      font-stretch: normal;
      line-height: 1.54;
      letter-spacing: normal;
      color: #6d7179;
      margin: 17px 0 0 0;
      overflow: hidden;
      text-overflow: ellipsis;
      @include respond-down(md) {
        margin: 4px 0 0 0;

        font-size: 12px;
      }
    }
  }

  &__action {
    margin-top: auto;
    margin-left: -2px;
    position: relative;
    top: 1px;

    @include respond-down(lg) {
      .primary-button {
        padding: 9.5px 35px;
      }
    }
    @include respond-down(md) {
      margin-top: 0 !important;
    }
  }
}
</style>
